import { useEffect, useState } from 'react';
import { appeler, ErreurApi } from './api';
import { PlanDeTravail } from './plan-travail';
import { MesAffaires } from './mes-affaires';
import { Icone } from './icones';

import { TITRES } from '../../src/partage/gestes-metier.js';

/**
 * L'ÉCRAN D'ARRIVÉE D'UN MÉTIER — et ses gestes à lui.
 *
 * ⚠️ LA TABLE DES GESTES A DÉMÉNAGÉ DANS `src/partage/gestes-metier.ts`,
 * et ce n'est pas un rangement : l'essai qui la garde doit pouvoir
 * l'IMPORTER, et `tsconfig.tests.json` ne configure pas `--jsx`. Importer
 * un `.tsx` depuis un essai passe au vert sous `vitest` (esbuild transpile
 * sans typer) et casse le typecheck du pré-vol — donc au moment de
 * déployer, pas au moment d'écrire.
 */
interface GesteEvalue {
  libelle: string; rubrique: string; aide: string; exige: string;
  ouverte: boolean; motif: string | null;
}
interface Habilitation {
  role: string; portee: string; division: string | null; motif: string | null;
  valide_du: string; valide_au: string | null; jours_restants: number | null;
  accordee_par: string | null;
}
interface BureauDuMetier {
  mode: string;
  autorite: {
    organisation: string | null; organisationType: string | null;
    habilitations: Habilitation[]; avertissement: string | null;
  };
  gestes: GesteEvalue[];
  resume: { ouverts: number; total: number };
}

export function BureauMetier(
  { modeCode, onOuvrirRubrique, onOuvrirAffaire, onReprendreSaisie, children }:
  {
    modeCode: string; onOuvrirRubrique: (rubrique: string) => void;
    /**
     * ⚠️ LE BUREAU DU MÉTIER EST CELUI QUE VOIT LA MAJORITÉ DES AGENTS —
     * collectivité, État, urbanisme, fiscal, investisseur. `Bureau` n'est monté
     * que pour le mode `notaire`. Poser la porte vers les affaires dans le seul
     * bureau du notaire l'aurait laissée fermée pour tous les autres, y compris
     * le chef du service foncier d'une commune.
     */
    onOuvrirAffaire: (reference: string) => void;
    onReprendreSaisie?: (typeCode: string, parcelle: string | null) => void;
    children?: React.ReactNode;
  },
) {
  const [d, setD] = useState<BureauDuMetier | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    setD(null); setErreur(null);
    appeler<BureauDuMetier>(`/bureau/metier/${encodeURIComponent(modeCode)}`)
      .then((r) => { if (vivant) setD(r); })
      .catch((e: unknown) => {
        if (!vivant) return;
        setErreur(e instanceof ErreurApi ? e.message : String(e));
      });
    return () => { vivant = false; };
  }, [modeCode]);

  const gestes = d?.gestes ?? [];

  return (
    <div className="bureau">
      {/* Le FLUX : tout ce qui se lit. Le rail d'opérations lui est frère,
          jamais un élément de grille qui traverse ses rangées — c'est ce qui
          fabriquait une bande morte sous le titre. */}
      <div className="bureau-flux">
      <header className="bureau-tete">
        <h2><Icone nom="bureau" taille={20} /> {TITRES[modeCode] ?? 'Bureau'}</h2>
      </header>

      {/* ── SOUS QUELLE AUTORITÉ J'AGIS ────────────────────────────────────
          ⚠️ EN TÊTE, ET AVANT TOUTE ACTION. Un acte posé hors habilitation
          n'est pas un acte maladroit : il est nul, et sa nullité ne se
          découvre qu'au litige — des mois plus tard, contre un tiers de bonne
          foi. L'agent doit savoir à quel titre il agit AVANT de toucher quoi
          que ce soit, pas en relisant un rapport. */}
      {d && (
        <section className="autorite">
          <div className="autorite-tete">
            <strong>{d.autorite.organisation ?? 'Aucune organisation rattachée'}</strong>
            {d.autorite.organisationType && <span className="jeton">{d.autorite.organisationType}</span>}
            <span className="aide">{d.resume.ouverts} geste(s) ouvert(s) sur {d.resume.total}</span>
          </div>
          {d.autorite.habilitations.length === 0 ? (
            <p className="autorite-vide">Aucune habilitation valide à cette date.</p>
          ) : (
            <ul className="autorite-liste">
              {d.autorite.habilitations.map((h, i) => (
                <li key={`${h.role}-${i}`}>
                  <span className="jeton">{h.role}</span>
                  <span className="aide">
                    {h.portee === 'nationale' ? 'portée nationale' : `portée ${h.portee}`}
                    {h.division ? ` · ${h.division}` : ''}
                  </span>
                  {/* ⚠️ LA FIN DE VALIDITÉ SE DIT, ET SON APPROCHE SE COMPTE.
                      « valide jusqu'au 12/10 » n'alerte personne ; « expire
                      dans 9 jours » fait reporter l'ouverture d'un dossier. */}
                  {h.jours_restants === null
                    ? <span className="aide">sans terme</span>
                    : <span className={h.jours_restants <= 30 ? 'jeton ambre' : 'aide'}>
                        expire dans {h.jours_restants} j
                      </span>}
                  {/* ⚠️ LE MOTIF D'UNE PORTÉE NATIONALE EST RENDU À LA LECTURE.
                      La base l'exige à l'écriture ; il n'était relu nulle part.
                      Un pouvoir national dont personne ne peut relire la
                      justification est un pouvoir sans contrôle. */}
                  {h.portee === 'nationale' && h.motif && <span className="autorite-motif">« {h.motif} »</span>}
                </li>
              ))}
            </ul>
          )}
          {d.autorite.avertissement && (
            <p className="autorite-alerte" role="alert">{d.autorite.avertissement}</p>
          )}
        </section>
      )}

      {/* ── LA PORTE VERS LES AFFAIRES ──────────────────────────────────────
          ⚠️ JUSTE SOUS L'AUTORITÉ, ET AVANT LES GESTES. Le bandeau au-dessus
          dit déjà « les affaires vous sont attribuées par votre service » —
          et l'écran n'en montrait aucune. Un texte qui nomme une chose absente
          de la page se lit comme une panne, ou comme une promesse. */}
      <MesAffaires onOuvrirAffaire={onOuvrirAffaire} onReprendreSaisie={onReprendreSaisie} />
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      <PlanDeTravail modeCode={modeCode} onOuvrirRubrique={onOuvrirRubrique} encastre />

      {children}

      </div>

      <section className="bureau-intentions">
        <h3>Ouvrir une opération</h3>
        {/* ⚠️ UN MODE SANS GESTE LE DIT, il n'affiche pas une rangée vide.
            Une section vide se lit « il n'y a rien à faire » ; la vérité serait
            « personne n'a encore déclaré ce que ce métier fait ici ». */}
        {gestes.length === 0 ? (
          <p className="indetermine">
            Aucun geste n’est encore déclaré pour ce métier. Ce n’est pas « rien à faire » :
            c’est « son OS reste à écrire ».
          </p>
        ) : (
          <div className="bureau-boutons">
            {gestes.map((g) => (
              <button key={g.libelle} type="button" className="bouton discret"
                      disabled={!g.ouverte}
                      /* ⚠️ UN GESTE FERMÉ EST AFFICHÉ ET MOTIVÉ, JAMAIS ABSENT.
                         Absent, l'agent conclut que la fonction n'existe pas et
                         va la chercher ailleurs ; fermé et motivé, il sait ce
                         qui lui manque et à qui le demander. */
                      title={g.ouverte ? g.aide : (g.motif ?? 'non accordé à vos rôles')}
                      onClick={() => onOuvrirRubrique(g.rubrique)}>
                {g.libelle}
                {!g.ouverte && <span className="jeton">fermé</span>}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
